import { C as Component } from "./component-BFVg3Q7H.js";
import { d as defineCustomElement } from "./utilities-BToXpjgA.js";
import { E as EmblaCarousel } from "./embla-DjVGOkZN.js";
class CarouselEmbla extends Component {
  requiredRefs = ["viewport", "container"];
  /** @type {import('embla-carousel').EmblaCarouselType} */
  embla = null;
  /** @type {ResizeObserver} */
  resizeObserver = null;
  /** @type {MutationObserver} */
  mutationObserver = null;
  /** @type {number | null} */
  #mutationTimeout = null;
  /** @type {number | null} */
  #initialSelectTimeout = null;
  /** @type {number | null} */
  #autoplayInterval = null;
  /** @type {number | null} */
  #resumeTimeout = null;
  /** @type {boolean} */
  #isPointerDown = false;
  /** @type {boolean} */
  #pendingReInit = false;
  /** @type {boolean} */
  #autoplayEnabled = false;
  /** @type {number} */
  #autoplayDelay = 5e3;
  connectedCallback() {
    super.connectedCallback();
    this.#initEmbla();
    this.refs.viewport.addEventListener("pointerdown", this.#handlePointerDown, {
      signal: this.signal
    });
    window.addEventListener("pointerup", this.#handlePointerUp, { signal: this.signal });
    window.addEventListener("pointercancel", this.#handlePointerUp, { signal: this.signal });
    this.resizeObserver = new ResizeObserver(() => {
      this.#scheduleReInit();
    });
    this.resizeObserver.observe(this.refs.viewport);
    this.mutationObserver = new MutationObserver((mutations) => {
      const relevantMutation = mutations.some((m) => {
        if (m.type !== "attributes" || m.attributeName !== "class") {
          return false;
        }
        if (!(m.target instanceof Element)) {
          return false;
        }
        return m.target.classList.contains("carousel-slide") || !!m.target.closest(".carousel-slide");
      });
      if (relevantMutation) {
        this.#scheduleReInit();
      }
    });
    this.mutationObserver.observe(this.refs.container, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class"]
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.embla?.destroy();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.#stopAutoplay();
    if (this.#mutationTimeout) {
      clearTimeout(this.#mutationTimeout);
    }
    if (this.#initialSelectTimeout) {
      clearTimeout(this.#initialSelectTimeout);
    }
  }
  get thumbnails() {
    return Array.isArray(this.refs.thumbnails) ? this.refs.thumbnails : [];
  }
  #initEmbla() {
    const options = {
      loop: this.dataset.loop !== "false",
      align: this.dataset.align || "start",
      containScroll: "trimSnaps",
      skipSnaps: true,
      // Skip snaps for slides with display:none (width: 0)
      // IMPORTANT: Filter out hidden slides (variant image switching uses `.media-hidden`).
      // Embla v9 measures slides via offsetWidth/offsetLeft; `display:none` slides produce 0 rects
      // and can break snapping/dragging. Using a selector keeps hidden slides in DOM but out of Embla.
      container: this.refs.container,
      slides: ".carousel-slide:not(.media-hidden):not(.is-hidden)",
      dragFree: this.dataset.dragFree === "true",
      slidesToScroll: parseInt(this.dataset.slidesToScroll) || 1,
      // Embla v9: New option names (watchDrag → draggable, etc.)
      draggable: true,
      resize: true,
      focus: true,
      slideChanges: true
    };
    this.embla = EmblaCarousel(this.refs.viewport, options);
    this.#setupArrows();
    this.#setupOverlayCounter();
    this.#setupDots();
    this.#setupThumbnails();
    this.#setupProgressbar();
    this.#setupAutoplay();
    this.#onSelect();
    this.embla.on("select", () => this.#onSelect());
    this.embla.on("reinit", () => this.#onSelect());
    this.embla.on("scroll", () => this.#updateProgressbar());
    this.embla.on("scrolloptimize", (emblaApi, event) => {
      const { willChange, translate3d } = event.detail;
      if (this.dataset.debug === "true") {
        console.log("[Carousel] Scroll optimization:", { willChange, translate3d });
      }
    });
    this.#initialSelectTimeout = setTimeout(() => {
      this.#onSelect();
    }, 100);
  }
  #scheduleReInit() {
    if (this.#isPointerDown) {
      this.#pendingReInit = true;
      return;
    }
    if (this.#mutationTimeout) {
      clearTimeout(this.#mutationTimeout);
    }
    this.#mutationTimeout = setTimeout(() => {
      if (!this.isConnected) {
        return;
      }
      if (!this.embla) {
        return;
      }
      const allSlides = this.embla.slideNodes();
      const visibleSlides = allSlides.filter(
        (s) => s.offsetParent !== null && !s.classList.contains("media-hidden") && !s.classList.contains("is-hidden")
      );
      const currentSnap = typeof this.embla.selectedSnap === "function" ? this.embla.selectedSnap() : 0;
      const currentSlide = allSlides[currentSnap];
      const currentIsVisible = currentSlide && currentSlide.offsetParent !== null && !currentSlide.classList.contains("media-hidden") && !currentSlide.classList.contains("is-hidden");
      const firstVisibleIndex = visibleSlides.length ? Math.max(0, allSlides.indexOf(visibleSlides[0])) : 0;
      const startSnap = currentIsVisible ? currentSnap : firstVisibleIndex;
      this.embla.reInit({
        startSnap,
        draggable: true,
        resize: true,
        focus: true,
        slideChanges: true
      });
    }, 50);
  }
  #handlePointerDown = () => {
    this.#isPointerDown = true;
  };
  #handlePointerUp = () => {
    this.#isPointerDown = false;
    if (this.#pendingReInit) {
      this.#pendingReInit = false;
      this.#scheduleReInit();
    }
  };
  #onSelect() {
    if (!this.embla) {
      return;
    }
    const slideCount = this.embla.slideNodes().length;
    this.dataset.singleSlide = slideCount <= 1 ? "true" : "false";
    this.#updateArrows();
    this.#updateOverlayCounter();
    this.#updateDots();
    this.#updateThumbnails();
    this.#updateCounter();
    this.#updateProgressbar();
  }
  // ARROWS
  #setupArrows() {
    if (!this.refs.prev || !this.refs.next || !this.embla) {
      return;
    }
    this.refs.prev.addEventListener(
      "click",
      () => {
        if (this.embla) {
          this.embla.goToPrev();
          this.#pauseAutoplayTemporarily();
        }
      },
      { signal: this.signal }
    );
    this.refs.next.addEventListener(
      "click",
      () => {
        if (this.embla) {
          this.embla.goToNext();
          this.#pauseAutoplayTemporarily();
        }
      },
      { signal: this.signal }
    );
  }
  #updateArrows() {
    if (!this.refs.prev || !this.refs.next || !this.embla) {
      return;
    }
    const allSlides = this.embla.slideNodes();
    const visibleSlides = allSlides.filter(
      (s) => s.offsetParent !== null && !s.classList.contains("media-hidden")
    );
    const isControlsArrow = this.refs.prev.classList.contains("carousel-controls-arrow");
    if (visibleSlides.length <= 1) {
      if (isControlsArrow) {
        this.refs.prev.disabled = true;
        this.refs.next.disabled = true;
      } else {
        this.refs.prev.style.display = "none";
        this.refs.next.style.display = "none";
      }
      return;
    }
    const shouldShow = this.#shouldShowArrows();
    if (!shouldShow) {
      if (isControlsArrow) {
        this.refs.prev.disabled = true;
        this.refs.next.disabled = true;
      } else {
        this.refs.prev.style.display = "none";
        this.refs.next.style.display = "none";
      }
      return;
    }
    const currentSnapIndex = this.embla.selectedSnap();
    const currentSlide = allSlides[currentSnapIndex];
    let currentVisibleIndex = visibleSlides.indexOf(currentSlide);
    if (currentVisibleIndex < 0) {
      const visibleRealIndices = visibleSlides.map((s) => allSlides.indexOf(s)).filter((i) => i >= 0);
      let best = 0;
      for (let i = 0; i < visibleRealIndices.length; i++) {
        if (visibleRealIndices[i] <= currentSnapIndex) {
          best = i;
        }
      }
      currentVisibleIndex = best;
    }
    const isLoop = this.dataset.loop !== "false";
    if (isLoop) {
      if (isControlsArrow) {
        this.refs.prev.disabled = false;
        this.refs.next.disabled = false;
      } else {
        this.refs.prev.style.display = "";
        this.refs.next.style.display = "";
      }
      return;
    }
    if (isControlsArrow) {
      this.refs.prev.disabled = currentVisibleIndex <= 0;
      this.refs.next.disabled = currentVisibleIndex >= visibleSlides.length - 1;
    } else {
      this.refs.prev.style.display = currentVisibleIndex <= 0 ? "none" : "";
      this.refs.next.style.display = currentVisibleIndex >= visibleSlides.length - 1 ? "none" : "";
    }
  }
  #shouldShowArrows() {
    if (!this.embla) {
      return true;
    }
    const allSlides = this.embla.slideNodes();
    const visibleSlides = allSlides.filter(
      (s) => s.offsetParent !== null && !s.classList.contains("media-hidden") && !s.classList.contains("is-hidden")
    );
    if (visibleSlides.length <= 1) {
      return false;
    }
    const viewportEl = this.refs.viewport;
    const containerEl = this.refs.container;
    const viewportWidth = viewportEl?.clientWidth ?? 0;
    if (viewportWidth > 0 && containerEl) {
      const overflow = containerEl.scrollWidth - viewportWidth;
      if (overflow <= 1) {
        return false;
      }
      return true;
    }
    const snapList = this.embla.snapList();
    return snapList.length > 1;
  }
  // DOTS
  #setupDots() {
    const dotsContainer = this.querySelector(".carousel-dots");
    if (!dotsContainer || !this.embla) {
      return;
    }
    this.#updateDots();
  }
  #updateDots() {
    const dotsContainer = this.querySelector(".carousel-dots");
    if (!dotsContainer || !this.embla) {
      return;
    }
    const allSlides = this.embla.slideNodes();
    const visibleSlides = allSlides.filter(
      (s) => s.offsetParent !== null && !s.classList.contains("media-hidden")
    );
    const snapList = this.embla.snapList();
    const dotCount = snapList.length;
    if (visibleSlides.length <= 1) {
      dotsContainer.style.display = "none";
      return;
    }
    const shouldShow = this.#shouldShowArrows();
    if (!shouldShow) {
      dotsContainer.style.display = "none";
      return;
    }
    dotsContainer.style.display = "";
    const currentSnapIndex = this.embla.selectedSnap();
    const activeDotIndex = Math.max(0, Math.min(currentSnapIndex, dotCount - 1));
    const existingDots = Array.from(dotsContainer.querySelectorAll(".carousel-dot"));
    const needsRegeneration = existingDots.length !== dotCount;
    if (needsRegeneration) {
      dotsContainer.innerHTML = Array.from({ length: dotCount }, (_, index) => {
        const slideNum = index + 1;
        const isActive = index === activeDotIndex;
        const snapIndex = index;
        const slideId = `carousel-snap-${snapIndex}`;
        return `<button
          type="button"
          class="carousel-dot${isActive ? " is-active" : ""}"
          role="tab"
          aria-label="Go to slide ${slideNum}"
          aria-selected="${isActive ? "true" : "false"}"
          aria-controls="${slideId}"
          tabindex="${isActive ? "0" : "-1"}"
        ></button>`;
      }).join("");
      const dots = Array.from(dotsContainer.querySelectorAll(".carousel-dot"));
      dots.forEach((dot, dotIndex) => {
        dot.addEventListener(
          "click",
          () => {
            if (!this.embla) {
              return;
            }
            this.embla.goTo(dotIndex);
            this.#pauseAutoplayTemporarily();
          },
          false
        );
      });
    } else {
      const dots = existingDots;
      if (dots.length > 0) {
        dots.forEach((dot, index) => {
          const isActive = index === activeDotIndex;
          dot.setAttribute("aria-selected", String(isActive));
          dot.classList.toggle("is-active", isActive);
        });
      }
    }
  }
  // OVERLAY COUNTER (combined arrows + counter inside viewport)
  #setupOverlayCounter() {
    if (!this.refs.overlayPrev || !this.refs.overlayNext || !this.embla) {
      return;
    }
    this.refs.overlayPrev.addEventListener(
      "click",
      () => {
        if (this.embla) {
          this.embla.goToPrev();
          this.#pauseAutoplayTemporarily();
        }
      },
      { signal: this.signal }
    );
    this.refs.overlayNext.addEventListener(
      "click",
      () => {
        if (this.embla) {
          this.embla.goToNext();
          this.#pauseAutoplayTemporarily();
        }
      },
      { signal: this.signal }
    );
  }
  #updateOverlayCounter() {
    const { overlayCurrent, overlayTotal, overlayPrev, overlayNext } = this.refs;
    if (!overlayCurrent || !this.embla) {
      return;
    }
    const slides = this.embla.slideNodes();
    const visibleSlides = slides.filter(
      (s) => s.offsetParent !== null && !s.classList.contains("media-hidden")
    );
    const overlayContainer = this.querySelector(".carousel-overlay-counter");
    if (overlayContainer && visibleSlides.length <= 1) {
      overlayContainer.style.display = "none";
      return;
    }
    if (overlayContainer) {
      overlayContainer.style.display = "";
    }
    const currentSlide = slides[this.embla.selectedSnap()];
    const visibleIndex = visibleSlides.indexOf(currentSlide);
    if (overlayCurrent instanceof HTMLElement) {
      overlayCurrent.textContent = String(visibleIndex + 1);
    }
    if (overlayTotal instanceof HTMLElement) {
      overlayTotal.textContent = String(visibleSlides.length);
    }
    if (overlayPrev) {
      overlayPrev.disabled = visibleIndex <= 0;
    }
    if (overlayNext) {
      overlayNext.disabled = visibleIndex >= visibleSlides.length - 1;
    }
  }
  // COUNTER
  #updateCounter() {
    const { current, total } = this.refs;
    if (!current || !this.embla) {
      return;
    }
    const slides = this.embla.slideNodes();
    const visibleSlides = slides.filter(
      (s) => s.offsetParent !== null && !s.classList.contains("media-hidden")
    );
    const counterContainer = this.querySelector(".carousel-counter")?.closest(".carousel-controls");
    if (counterContainer && visibleSlides.length <= 1) {
      counterContainer.style.display = "none";
      return;
    }
    if (counterContainer) {
      const shouldShow = this.#shouldShowArrows();
      if (!shouldShow) {
        counterContainer.style.display = "none";
        return;
      }
      counterContainer.style.display = "";
    }
    const currentSlide = slides[this.embla.selectedSnap()];
    const visibleIndex = visibleSlides.indexOf(currentSlide);
    const slideCount = visibleSlides.length;
    if (current instanceof HTMLElement) {
      current.textContent = String(visibleIndex + 1);
    }
    if (total instanceof HTMLElement) {
      total.textContent = String(slideCount);
    }
  }
  // PROGRESSBAR
  #setupProgressbar() {
  }
  #updateProgressbar() {
    const { progressbar } = this.refs;
    if (!progressbar || !this.embla) {
      return;
    }
    const slides = this.embla.slideNodes();
    const visibleSlides = slides.filter(
      (s) => s.offsetParent !== null && !s.classList.contains("media-hidden")
    );
    const progressbarContainer = this.querySelector(".carousel-progressbar")?.closest(".carousel-controls");
    if (progressbarContainer && visibleSlides.length <= 1) {
      progressbarContainer.style.display = "none";
      return;
    }
    if (progressbarContainer) {
      const shouldShow = this.#shouldShowArrows();
      if (!shouldShow) {
        progressbarContainer.style.display = "none";
        return;
      }
      progressbarContainer.style.display = "";
    }
    const emblaAny = this.embla;
    const rawProgress = typeof emblaAny.scrollProgress === "function" ? emblaAny.scrollProgress() : null;
    let progress = 0;
    if (typeof rawProgress === "number" && !Number.isNaN(rawProgress)) {
      const clamped = Math.max(0, Math.min(1, rawProgress));
      progress = clamped * 100;
    } else {
      const snapCount = this.embla.snapList()?.length ?? 0;
      const currentSnapIndex = this.embla.selectedSnap?.() ?? 0;
      progress = snapCount > 1 ? Math.max(0, Math.min(snapCount - 1, currentSnapIndex)) / (snapCount - 1) * 100 : 0;
    }
    if (progressbar instanceof HTMLElement) {
      progressbar.style.width = `${progress}%`;
    }
  }
  // THUMBNAILS
  #setupThumbnails() {
    if (!this.thumbnails.length || !this.embla) {
      return;
    }
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener(
        "click",
        () => {
          if (!this.embla) {
            return;
          }
          const thumbPos = thumb.getAttribute("data-media-position");
          if (thumbPos) {
            const slides = this.embla.slideNodes();
            const slideIndex = slides.findIndex(
              (s) => s.getAttribute("data-media-position") === thumbPos
            );
            if (slideIndex >= 0) {
              this.embla.goTo(slideIndex);
              this.#pauseAutoplayTemporarily();
              return;
            }
          }
          this.embla.goTo(index);
          this.#pauseAutoplayTemporarily();
        },
        { signal: this.signal }
      );
    });
  }
  #updateThumbnails() {
    if (!this.thumbnails.length || !this.embla) {
      return;
    }
    const slides = this.embla.slideNodes();
    const currentSnapIndex = this.embla.selectedSnap();
    const currentSlide = slides[currentSnapIndex];
    const currentPos = currentSlide?.getAttribute("data-media-position") || null;
    this.thumbnails.forEach((thumb, index) => {
      const isActive = currentPos ? thumb.getAttribute("data-media-position") === currentPos : index === currentSnapIndex;
      thumb.setAttribute("aria-selected", String(isActive));
      thumb.setAttribute("tabindex", isActive ? "0" : "-1");
      thumb.classList.toggle("is-active", isActive);
    });
    const activeThumbnail = (currentPos ? this.thumbnails.find((t) => t.getAttribute("data-media-position") === currentPos) : this.thumbnails[currentSnapIndex]) || null;
    if (activeThumbnail) {
      this.#centerThumbnailInRail(activeThumbnail);
    }
  }
  /**
   * Center the active thumbnail inside its own scroll rail (.carousel-thumbnails).
   * Deliberately does NOT use scrollIntoView: that also scrolls the window/document
   * (block:"nearest"), which on a scrolled-down PDP yanks the page up to the gallery
   * whenever the active slide changes programmatically (e.g. variant/swatch switch).
   * Scrolling the rail element directly keeps the page position untouched.
   */
  #centerThumbnailInRail(thumb) {
    const rail = thumb.closest(".carousel-thumbnails");
    if (!rail) {
      return;
    }
    const thumbRect = thumb.getBoundingClientRect();
    const railRect = rail.getBoundingClientRect();
    if (rail.scrollWidth > rail.clientWidth + 1) {
      const delta = thumbRect.left - railRect.left - (rail.clientWidth - thumbRect.width) / 2;
      rail.scrollBy({ left: delta, behavior: "instant" });
    } else if (rail.scrollHeight > rail.clientHeight + 1) {
      const delta = thumbRect.top - railRect.top - (rail.clientHeight - thumbRect.height) / 2;
      rail.scrollBy({ top: delta, behavior: "instant" });
    }
  }
  // AUTOPLAY
  #setupAutoplay() {
    if (this.dataset.autoplay !== "true" || !this.embla) {
      return;
    }
    const slideCount = this.embla.snapList().length;
    if (slideCount <= 1) {
      return;
    }
    this.#autoplayEnabled = true;
    this.#autoplayDelay = parseInt(this.dataset.autoplayDelay) || 5e3;
    this.refs.viewport.addEventListener(
      "pointerenter",
      (e) => {
        if (e.pointerType === "mouse") {
          this.#pauseAutoplay();
        }
      },
      { signal: this.signal }
    );
    this.refs.viewport.addEventListener(
      "pointerleave",
      (e) => {
        if (e.pointerType === "mouse") {
          this.#resumeAutoplay();
        }
      },
      { signal: this.signal }
    );
    this.#startAutoplay();
  }
  #startAutoplay() {
    if (!this.#autoplayEnabled || this.#autoplayInterval) {
      return;
    }
    this.#autoplayInterval = setInterval(() => {
      if (this.embla.canGoToNext()) {
        this.embla.goToNext();
      } else {
        this.embla.goTo(0);
      }
    }, this.#autoplayDelay);
  }
  #pauseAutoplay() {
    if (this.#autoplayInterval) {
      clearInterval(this.#autoplayInterval);
      this.#autoplayInterval = null;
    }
  }
  #stopAutoplay() {
    this.#autoplayEnabled = false;
    this.#pauseAutoplay();
    this.#clearResumeTimeout();
  }
  #pauseAutoplayTemporarily() {
    if (!this.#autoplayEnabled) {
      return;
    }
    this.#pauseAutoplay();
    this.#clearResumeTimeout();
    this.#resumeTimeout = setTimeout(() => {
      this.#startAutoplay();
    }, 3e3);
  }
  #resumeAutoplay() {
    if (!this.#autoplayEnabled) {
      return;
    }
    this.#clearResumeTimeout();
    this.#resumeTimeout = setTimeout(() => {
      this.#startAutoplay();
    }, 1e3);
  }
  #clearResumeTimeout() {
    if (this.#resumeTimeout) {
      clearTimeout(this.#resumeTimeout);
      this.#resumeTimeout = null;
    }
  }
}
defineCustomElement("carousel-embla", CarouselEmbla);
export {
  CarouselEmbla
};
